/* eslint-disable camelcase */
import React, { FC, useCallback, useEffect, useMemo, useState } from "react"
import { observer } from "mobx-react-lite"
import { Appearance, ListRenderItem, ListRenderItemInfo, ViewStyle, FlatList } from "react-native"
import { ActionsheetIosAware, ListEmpty, Screen, Text } from "../../../../components"
// import { useStores } from "../../models"
import { color, nativeBaseTheme } from "../../../../theme"
import { useStores } from "../../../../models"
import { FarmStoreModel } from "../../../../models/app-store/farm-store/farm-store"
import { BatchStoreModel } from "../../../../models/app-store/farm-store/batch-store/batch-store"
import { Actionsheet, View } from "native-base"
import numeral from "numeral"
import { translate } from "../../../../i18n"
import { SafeAreaView } from "react-native-safe-area-context"
import { InfoItem, ItemFooter, InfoWarningModal } from "../overview-feed-cost-list/overview-feed-cost-list-screen"
import { Currency } from "../../../../database"
import { useFocusEffect } from "@react-navigation/native"
import JalaAnalytic from "../../../../utils/jala-analytic"
import { getSnapshot } from "mobx-state-tree"

const ROOT: ViewStyle = {
  backgroundColor: color.palette.white,
  flex: 1,
}

const CONTENT_ITEM: ViewStyle = {
  borderBottomWidth: 1,
  borderColor: color.palette.gray200,
  flexDirection: 'row'
}

const Item: FC<{ colorScheme?: "light" | "dark", info: ListRenderItemInfo<any> }> = observer(function Item(props) {
  const { colorScheme, info } = props

  const { item: { age, pond_name, cycle_actuals } } = info;
  const actualCycle = useMemo(() => cycle_actuals.find((c) => c.age === age) || {}, [age, cycle_actuals])

  return (
    <View style={CONTENT_ITEM}>
      <View width="25%" paddingX="8px" py="10px" alignItems="flex-start">
        <Text ellipsizeMode="middle" text={`${pond_name} (${age})`} preset="body3" colorScheme={colorScheme} numberOfLines={1} />
      </View>
      <View width="25%" paddingX="8px" py="10px" alignItems="flex-end" backgroundColor={color.palette.gray100}>
        <Text text={numeral(actualCycle.rough_total_cost).format('0,0')} preset="body3" colorScheme={colorScheme} />
      </View>
      <View width="25%" paddingX="8px" py="10px" alignItems="flex-end">
        <Text text={numeral(actualCycle.feed_cost).format('0,0')} preset="body3" colorScheme={colorScheme} />
      </View>
      <View width="25%" paddingX="8px" py="10px" alignItems="flex-end">
        <Text text={numeral(actualCycle.rough_total_cost - actualCycle.feed_cost).format('0,0')} preset="body3" colorScheme={colorScheme} />
      </View>
    </View>
  )
});

const HEADER: ViewStyle = {
  borderTopWidth: 1,
  borderBottomWidth: 1,
  borderColor: color.palette.gray200,
  flexDirection: 'row',
  paddingVertical: 8,
  alignItems: 'center',
  backgroundColor: color.palette.white
}

const TableHeader: FC<{ colorScheme?: "light"|"dark" }> = observer((props) => {
  const { colorScheme } = props;
  const { appStore: { farmStore: { farm } }} = useStores();
  const [currency, setCurrency] = useState<string>()

  useEffect(() => {
    if (farm?.currency_id) {
      Currency.fetch({
        id__eq: farm?.currency_id
      }).then(models => {
        return models.map(model => model._attributes)
      }).then(values => {
        setCurrency(values.length ? values[0].symbol : farm?.currency_id)
      })
    }
  }, [farm])

  return (
    <View style={HEADER}>
      <View width="25%" paddingX="8px" alignItems="flex-start">
        <Text colorScheme={colorScheme} tx="overviewProductionCostEstimationListScreen.pond" preset="body3" color={color.palette.gray800} />
      </View>
      <View width="25%" paddingX="8px" alignItems="flex-end">
        <Text textAlign="center" colorScheme={colorScheme} tx="overviewProductionCostEstimationListScreen.productionCost" txOptions={{currency}} preset="body3" color={color.palette.gray800} />
      </View>
      <View width="25%" paddingX="8px" alignItems="flex-end">
        <Text textAlign="center" colorScheme={colorScheme} tx="overviewProductionCostEstimationListScreen.feedCost" txOptions={{currency}} preset="body3" color={color.palette.gray800} />
      </View>
      <View width="25%" paddingX="8px" alignItems="flex-end">
        <Text textAlign="center" colorScheme={colorScheme} tx="overviewProductionCostEstimationListScreen.otherCost" txOptions={{currency}} preset="body3" color={color.palette.gray800} />
      </View>
    </View>
  )
})

const Header: FC<{ colorScheme?: "light"|"dark" }> = observer(function Header(props) {
  const { colorScheme = "light" || Appearance.getColorScheme() } = props;
  const { appStore: { farmStore }} = useStores();
  const { activeBatchStore } = farmStore || FarmStoreModel.create({})
  const { predictionTotalRoughCost, cycles, isFeedActualWithin4Days, loadingCyclePrediction } = activeBatchStore || BatchStoreModel.create({})

  return (
    <View paddingX="16px" paddingTop="8px" paddingBottom="15px">
      <Text text={farmStore.farm.name} preset="title3" marginBottom="4px" colorScheme={colorScheme} />
      <Text tx="overviewProductionCostEstimationListScreen.subtitle" preset="body3" colorScheme={colorScheme} color={nativeBaseTheme.colors.gray[colorScheme === 'light' ? '700' : '300']} />
      <View flexDirection="row" alignItems="center">
        <Text
          text={numeral(predictionTotalRoughCost / 1000000).format('0,0.[00]')}
          preset="display1"
          marginRight="3px"
          colorScheme={colorScheme}
          color={loadingCyclePrediction && color.palette.dust500 || undefined}
        />
        <Text tx="million" preset="display2" marginRight="8px" colorScheme={colorScheme} color={color.palette.dust500} />
      </View>
      <Text text={translate("overviewScreen.fromActivePonds", { count: cycles.length })} preset="body3" colorScheme={colorScheme} color={color.palette.dust500} />
      {!isFeedActualWithin4Days && <InfoWarningModal />}
    </View>
  )
})

export const InfoModal: FC<{ colorScheme?: "light" | "dark", isOpen?: boolean, onClose?: () => void }> = observer(function InfoModal(props) {
  const { colorScheme, isOpen, onClose } = props

  return (
    <ActionsheetIosAware onClose={onClose} isOpen={isOpen}>
      <Actionsheet.Content>
        <View justifyContent={"flex-start"} marginX={"16px"}>
          <Text paddingTop={"8px"} colorScheme={colorScheme} preset="display3" tx="overviewProductionCostEstimationListScreen.infoPriceTitle" />
          <Text paddingTop={"8px"} colorScheme={colorScheme} preset="body2" tx="overviewProductionCostEstimationListScreen.infoPriceText" />
          <Text paddingTop={"8px"} colorScheme={colorScheme} preset="display3" tx="overviewProductionCostEstimationListScreen.infoPriceTitle2" />
          <Text paddingTop={"8px"} colorScheme={colorScheme} preset="body2" tx="overviewProductionCostEstimationListScreen.infoPriceText2" />
          <Text paddingTop={"8px"} colorScheme={colorScheme} preset="body2" tx="overviewProductionCostEstimationListScreen.infoPriceText3" />
        </View>
        <SafeAreaView edges={['bottom']} />
      </Actionsheet.Content>
    </ActionsheetIosAware>
  )
})

export const OverviewProductionCostEstimationListScreen: FC<{ colorScheme?: "light" | "dark" }> = observer(function OverviewProductionCostEstimationListScreen(props) {
  const { colorScheme = "light" || Appearance.getColorScheme() } = props;

  const { appStore: { farmStore }} = useStores();
  const { activeBatchStore } = farmStore || FarmStoreModel.create({})
  const { cycles, loadingCyclePrediction } = activeBatchStore || BatchStoreModel.create({})
  const keyExtractor = useCallback((item, index) => `${item.id || item.started_at || index}`, [])
  const [isOpenInfo, setOpenInfo] = useState<boolean>(false)
  const [showHeader, setShowHeader] = useState<boolean>(false)

  const renderItem: ListRenderItem<any> = useCallback((info) => {
    return <Item info={info} colorScheme={colorScheme} />
  }, [colorScheme])

  const onRefresh = useCallback(() => {
    activeBatchStore.init()
  }, [activeBatchStore])

  useFocusEffect(useCallback(() => {
    JalaAnalytic.log('Mobile app: Home - Overview - Production cost - View');
  }, []))

  return (
    <Screen
      style={ROOT}
      preset="fixed"
      unsafe
      colorScheme={colorScheme}
      backgroundBar={colorScheme === "light" ? color.primary : undefined}
      statusBar={colorScheme === 'light' ? 'light-content' : 'dark-content'}
      hideOnBlurred={false}
    >
      {showHeader && <TableHeader colorScheme={colorScheme} />}
      <FlatList
        data={getSnapshot(cycles)}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        ListHeaderComponent={
          <View backgroundColor={color.palette.white}>
            <Header colorScheme={colorScheme} />
            <InfoItem onPressMoreInfo={() => {setOpenInfo(true)}} tx="overviewProductionCostEstimationListScreen.infoCostFormula" colorScheme={colorScheme} />
            <TableHeader colorScheme={colorScheme} />
          </View>
        }
        ListEmptyComponent={<ListEmpty />}
        ListFooterComponent={<SafeAreaView edges={['bottom']} />}
        onRefresh={onRefresh}
        refreshing={!!loadingCyclePrediction}
        onScroll={({nativeEvent}) => {
          setShowHeader(nativeEvent.contentOffset.y>200)
        }}
      />
      <ItemFooter colorScheme={colorScheme} />
      <InfoModal colorScheme={colorScheme} isOpen={isOpenInfo} onClose={() => setOpenInfo(false)} />
    </Screen>
  )
})
